import 'styles/pages/features/agency.scss';
import '~scripts/main';

// Packages
import Vue from 'vue';

// Swiper
import Swiper from 'swiper';
import { EffectFade, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([EffectFade, SwiperNavigation, SwiperPagination]);

// Core
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { isInViewport } from '~scripts/core/helpers';
import { YouTubePlayer } from '~scripts/core/YouTubePlayer';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';
import initSubscribeForm from '~scripts/blocks/subscribe-form';
import initRequestForm from '~scripts/blocks/request-form';
import ClientsLogotypes from '~scripts/blocks/vue/ClientsLogotypes.vue';

// Components
import '~scripts/components/modals';
import initTextAnimation from '~scripts/components/text-animation';

// Breakpoints
const mediaMd = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin})`);

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// TEXT ANIMATION
//
initTextAnimation('.js-intro-text-animation', mediaMd);

//
// CLIENTS
//
new Vue({
  el: '#VClientsLogotypes',
  name: 'VClientsLogotypes',
  components: {
    ClientsLogotypes,
  },
});

//
// VIDEO
//
const handlePlayButtonClick = (button, playerList) => {
  button.classList.add('video__thumb--hidden');
  try {
    playerList['player-0'].playVideo();
  } catch (error) {
    console.warn('YouTube player is not ready');
  }
};

const handleWindowScroll = (button, playerList) => {
  if (!isInViewport(button)) {
    if (button.classList.contains('video__thumb--hidden')) {
      button.classList.remove('video__thumb--hidden');
      playerList['player-0'].pauseVideo();
    }
  }
};

const playerOptions = {
  videoElements: '.js-video',
  playButtonSelector: '.js-play-video',
  handlePlayButtonClick: handlePlayButtonClick,
  handleWindowScroll: handleWindowScroll,
};

const player = await new YouTubePlayer(playerOptions);
player.init();

//
// FEATURES
//
// Variables
const header = document.getElementById('header');

let featuresVideoPlayVideoFromOffset = 0;
let featuresVideoAdjacentSlides = true;
let featuresVideoPrevIndex = 0;

const featuresVideoEnable = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin})`);
const featuresVideo = document.getElementById('featuresVideo');
const featuresVideoForwards = document.getElementById('featuresVideoForwards');
const featuresVideoForwardsTimecodes = [
  [0, 0],
  [0, 0.75],
  [0.75, 1.75],
  [1.75, 3],
];
const featuresVideoBackwards = document.getElementById('featuresVideoBackwards');
const featuresVideoBackwardsTimecodes = [
  [2.25, 3],
  [1.25, 2.25],
  [0, 1.25],
  [0, 0],
];
const featuresItems = Array.from(document.querySelectorAll('.js-feature-item'));
const featuresPagination = document.getElementById('featuresPagination');
const featuresPaginationButtons = featuresPagination.querySelectorAll('button');

// Methods
const featuresHelpers = {
  showVideoForwards: () => {
    featuresVideoForwards.classList.remove('is-hidden');
    featuresVideoForwards.removeAttribute('aria-hidden');
  },
  hideVideoForwards: () => {
    featuresVideoForwards.classList.add('is-hidden');
    featuresVideoForwards.setAttribute('aria-hidden', 'true');
  },
  showVideoBackwards: () => {
    featuresVideoBackwards.classList.remove('is-hidden');
    featuresVideoBackwards.removeAttribute('aria-hidden');
  },
  hideVideoBackwards: () => {
    featuresVideoBackwards.classList.add('is-hidden');
    featuresVideoBackwards.setAttribute('aria-hidden', 'true');
  },
  toggleVideos: function (isScrollFwd) {
    if (isScrollFwd) {
      this.hideVideoBackwards();
      this.showVideoForwards();
    } else {
      this.hideVideoForwards();
      this.showVideoBackwards();
    }
  },
  handleVideoTop: () => {
    if (window.matchMedia(`(max-height: 800px)`).matches) {
      featuresVideo.style.removeProperty('top');
    } else {
      featuresVideo.style.top = `${
        window.innerHeight / 2 - featuresVideo.offsetHeight / 2 + header.offsetHeight / 2
      }px`;
    }

    if (featuresVideo.classList.contains('features__video--hidden')) {
      featuresVideo.classList.remove('features__video--hidden');
    }
  },
  setActivePaginationButton: (activeIndex) => {
    featuresPaginationButtons.forEach((item, index) => {
      if (activeIndex === index) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
  },
  gotoSlide: (isScrollFwd, index) => {
    const video = isScrollFwd ? featuresVideoForwards : featuresVideoBackwards;
    let [, prevEnd] = isScrollFwd
      ? featuresVideoForwardsTimecodes[featuresVideoPrevIndex]
      : featuresVideoBackwardsTimecodes[featuresVideoPrevIndex];
    const [start, end] = isScrollFwd ? featuresVideoForwardsTimecodes[index] : featuresVideoBackwardsTimecodes[index];

    if (featuresVideoPrevIndex === index) {
      prevEnd = start;
    }

    // Setting active pagination bullet
    featuresHelpers.setActivePaginationButton(index);

    // Fire!
    playVideoFromTo(video, prevEnd, end, isScrollFwd);

    featuresVideoPrevIndex = index;
  },
};

// Functions
function playVideoFromTo(video, from, to, isScrollFwd) {
  video.pause();
  from = from + featuresVideoPlayVideoFromOffset;
  video.currentTime = from;

  // https://stackoverflow.com/a/37172024
  // Preventing race condition between .pause() and .play()
  setTimeout(() => {
    const playPromise = video.play();

    playPromise
      .then(() => {
        featuresHelpers.toggleVideos(isScrollFwd);

        const interval = setInterval(() => {
          if (featuresVideoAdjacentSlides) {
            featuresVideoPlayVideoFromOffset = Math.round((to - video.currentTime + Number.EPSILON) * 100) / 100;
          }

          if (video.currentTime >= to) {
            clearInterval(interval);
            if (featuresVideoAdjacentSlides) {
              featuresVideoPlayVideoFromOffset = 0;
            }
            video.pause();
          }
        }, 0);
      })
      .catch(() => {});
  }, 100);
}

// Handlers
const handleFeaturesLoad = () => {
  featuresHelpers.handleVideoTop();
};

const handleFeaturesScroll = () => {
  // Detecting item in viewport
  const featuresVideoHeight = featuresVideo.offsetHeight;
  const featuresVideoTop = featuresVideo.getBoundingClientRect().top;
  const scrollThreshold = featuresVideoTop + featuresVideoHeight * 0.5;
  let index = -1;

  for (let i = featuresItems.length - 1; i >= 0; i -= 1) {
    const item = featuresItems[i];
    const itemTop = item.getBoundingClientRect().top;

    if (itemTop < scrollThreshold) {
      index = i;
      break;
    }
  }

  // Detecting direction
  const isScrollFwd = index > featuresVideoPrevIndex;

  // Playing video on scroll
  if (index !== -1 && index !== featuresVideoPrevIndex) {
    // Playing video
    featuresHelpers.gotoSlide(isScrollFwd, index);
  }
};

const handleFeaturesResize = () => {
  featuresHelpers.handleVideoTop();
};

const handlePaginationButtonsClick = (index) => {
  // Scrolling page
  const item = featuresItems[index];
  const itemTop = item.offsetTop;
  const itemHeight = item.offsetHeight;

  featuresVideoAdjacentSlides = Math.abs(featuresVideoPrevIndex - index) === 1;

  window.scrollTo({
    top: itemTop - ((window.innerHeight - itemHeight) / 2 + header.offsetHeight / 2),
    left: 0,
    behavior: 'smooth',
  });

  // Video will play on 'scroll' event listener
};

const handleFeaturesInit = () => {
  if (featuresVideoEnable.matches) {
    featuresHelpers.handleVideoTop();

    window.addEventListener('scroll', handleFeaturesScroll);
    window.addEventListener('resize', handleFeaturesResize);

    featuresPaginationButtons.forEach((button, index) => {
      button.addEventListener('click', handlePaginationButtonsClick.bind(null, index));
    });
  } else {
    window.removeEventListener('scroll', handleFeaturesScroll);
    window.removeEventListener('resize', handleFeaturesResize);

    featuresPaginationButtons.forEach((button) => {
      button.removeEventListener('click', handlePaginationButtonsClick);
    });
  }
};

// Init
handleFeaturesInit();
window.addEventListener('load', handleFeaturesLoad);
featuresVideoEnable.addEventListener('change', handleFeaturesInit);

//
// FEEDBACKS
//
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);

const feedbacksSliderOptionsBase = {
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
};
const feedbacksSliderOptionsSm = {
  allowTouchMove: true,
  effect: 'slide',
  loop: false,
  navigation: false,
  noSwiping: false,
  slidesPerView: 'auto',
  slidesOffsetBefore: 10,
  slidesOffsetAfter: 10,
  spaceBetween: 16,
};
const feedbacksSliderOptionsMd = {
  allowTouchMove: false,
  effect: 'fade',
  loop: true,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  noSwiping: true,
  slidesPerView: 1,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  spaceBetween: 0,
};

let feedbacksSlider = null;

const handleFeedbacksSliderInit = () => {
  if (feedbacksSlider) {
    feedbacksSlider.destroy();
  }

  const options = mediaSm.matches ? feedbacksSliderOptionsSm : feedbacksSliderOptionsMd;

  feedbacksSlider = new Swiper('#feedbacksSlider', Object.assign(feedbacksSliderOptionsBase, options));
};

handleFeedbacksSliderInit();
mediaSm.addEventListener('change', handleFeedbacksSliderInit);

//
// DOWNLOAD PDF FORM
//
const downloadPdfForm = document.getElementById('downloadPdfForm');

initSubscribeForm(downloadPdfForm);

//
// DEMO REQUEST FORM
//
const demoRequestForms = document.querySelectorAll('.js-demo-request-form');

demoRequestForms.forEach((form) => {
  initRequestForm(form);
});
